'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { LeadCaptureForm } from '@/components/calculator/LeadCaptureForm';

// Each option carries a risk weight (higher = more risk) and a gap flag that
// surfaces a specific recommendation when chosen.
interface Option {
  text: string;
  risk: number; // 0 (good) .. 3 (worst)
  gap?: string; // recommendation triggered when this option is selected
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: 'frequency',
    text: 'How often do employees get security awareness training?',
    helpText: 'Annual-only training is forgotten within weeks.',
    options: [
      { text: 'Ongoing—short, regular training year-round', risk: 0 },
      { text: 'Once a year (annual compliance training)', risk: 2, gap: 'Annual-only training is forgotten within weeks. Move to short, continuous training that keeps security top of mind.' },
      { text: 'Rarely or never / not sure', risk: 3, gap: 'Untrained staff are the easiest way in. Launching a continuous awareness program is the highest-leverage human-risk fix.' },
    ],
  },
  {
    id: 'phishing_sims',
    text: 'Do you run simulated phishing tests?',
    helpText: 'Simulations measure and reduce click rates.',
    options: [
      { text: 'Yes—regular simulations with follow-up training', risk: 0 },
      { text: 'Tried it once or occasionally', risk: 1, gap: 'One-off phishing tests don’t build resilience. Run regular simulations and auto-assign training to clickers.' },
      { text: 'Never run a phishing simulation / not sure', risk: 2, gap: 'Without simulations you can’t measure or reduce click rates. Start a recurring program.' },
    ],
  },
  {
    id: 'onboarding',
    text: 'Do new hires get security training when they start?',
    helpText: 'New hires are targeted early.',
    options: [
      { text: 'Yes—security training is part of onboarding', risk: 0 },
      { text: 'Eventually, not at onboarding', risk: 1, gap: 'New hires are targeted early. Deliver security training on day one.' },
      { text: 'No onboarding training / not sure', risk: 2, gap: 'Untrained new hires are prime targets for impersonation. Add security to onboarding.' },
    ],
  },
  {
    id: 'reporting',
    text: 'Can employees easily report a suspicious email, and do they?',
    helpText: 'Friction kills reporting.',
    options: [
      { text: 'Yes—one-click reporting and a strong reporting culture', risk: 0 },
      { text: 'They can forward to IT, but rarely do', risk: 1, gap: 'Friction kills reporting. Add a one-click report button and encourage reporting so threats surface fast.' },
      { text: 'No reporting mechanism / not sure', risk: 2, gap: 'If staff can’t easily report phishing, attacks go unnoticed. Deploy a report button and track reporting rates.' },
    ],
  },
  {
    id: 'metrics',
    text: 'Do you track click rates and improvement over time?',
    helpText: 'Unmeasured awareness can’t be improved.',
    options: [
      { text: 'Yes—we track and report on human-risk metrics', risk: 0 },
      { text: 'Some data but not really tracked', risk: 1, gap: 'Without trend metrics you can’t show progress or target repeat clickers. Track click/report rates over time.' },
      { text: 'No metrics / not sure', risk: 2, gap: 'A managed program reports click rates, reporting rates, and risk by department.' },
    ],
  },
  {
    id: 'high_risk',
    text: 'Do high-risk roles (finance, execs, IT) get extra training?',
    helpText: 'Finance and execs face targeted BEC and wire fraud.',
    options: [
      { text: 'Yes—role-based training for high-risk staff', risk: 0 },
      { text: 'Everyone gets the same generic training', risk: 1, gap: 'Finance and execs face targeted BEC. Add role-based training for high-risk groups.' },
      { text: 'No role-based approach / not sure', risk: 2, gap: 'Generic-only training underprepares your highest-value targets. Layer in role-specific modules for finance, execs, and IT.' },
    ],
  },
];

type Status = 'intro' | 'assessment' | 'results';

interface Answer {
  questionId: string;
  optionIndex: number;
}

function levelFor(percent: number): {
  level: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
  blurb: string;
} {
  if (percent >= 55) {
    return {
      level: 'High',
      color: 'text-red-700 dark:text-red-300',
      bg: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
      blurb:
        'Your people are your most-exposed attack surface right now. A continuous training and phishing-simulation program should be an immediate priority.',
    };
  }
  if (percent >= 25) {
    return {
      level: 'Medium',
      color: 'text-amber-700 dark:text-amber-300',
      bg: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
      blurb:
        'You have some training in place but meaningful gaps remain. Closing them—especially simulations, reporting, and metrics—will materially lower your phishing risk.',
    };
  }
  return {
    level: 'Low',
    color: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-950/40 border-green-200 dark:border-green-800',
    blurb:
      'Your human-risk posture is strong. A managed program keeps it that way—running continuous training, simulations, and reporting as your team grows.',
  };
}

export default function RiskCheckClient() {
  const [status, setStatus] = useState<Status>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [userCount, setUserCount] = useState<number>(25);

  const handleAnswer = (optionIndex: number) => {
    const question = QUESTIONS[currentIndex];
    const newAnswer: Answer = { questionId: question.id, optionIndex };
    const newAnswers = [...answers.filter((a) => a.questionId !== question.id), newAnswer];
    setAnswers(newAnswers);

    if (currentIndex < QUESTIONS.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStatus('results');
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) setCurrentIndex(currentIndex - 1);
  };

  const handleRestart = () => {
    setAnswers([]);
    setCurrentIndex(0);
    setStatus('intro');
  };

  const result = useMemo(() => {
    if (answers.length !== QUESTIONS.length) return null;
    let total = 0;
    const maxPerQuestion = 3;
    const gaps: string[] = [];
    QUESTIONS.forEach((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      if (!a) return;
      const option = q.options[a.optionIndex];
      total += option.risk;
      if (option.gap) gaps.push(option.gap);
    });
    const max = QUESTIONS.length * maxPerQuestion;
    const percent = Math.round((total / max) * 100);
    return { percent, gaps, ...levelFor(percent) };
  }, [answers]);

  const resultsSummary = useMemo(() => {
    if (!result) return '';
    const answerLines = QUESTIONS.map((q) => {
      const a = answers.find((ans) => ans.questionId === q.id);
      const chosen = a ? q.options[a.optionIndex].text : 'N/A';
      return `  • ${q.text} → ${chosen}`;
    }).join('\n');

    const gapLines = result.gaps.length
      ? result.gaps.map((g, i) => `${i + 1}. ${g}`).join('\n')
      : 'No major gaps identified—maintain current controls.';

    return `SECURITY AWARENESS & PHISHING RISK CHECK RESULTS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Human Risk: ${result.level} (${result.percent}% risk score)
Approx. employees: ${userCount}

${result.blurb}

ANSWERS:
${answerLines}

TOP GAPS TO CLOSE:
${gapLines}`;
  }, [result, answers, userCount]);

  // Intro
  if (status === 'intro') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="text-center mb-8">
            <div className="text-5xl mb-4">🎣</div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">How exposed is your team to phishing?</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto">
              Six questions on training cadence, phishing simulations, onboarding, reporting, metrics, and high-risk roles. Get an instant Human Risk score and your top gaps.
            </p>
          </div>

          <div className="bg-[#2813e8]/5 border border-[#2813e8]/20 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-[#2813e8] mb-2">What you&apos;ll learn:</h3>
            <ul className="text-gray-700 dark:text-gray-300 text-sm space-y-1">
              <li>• Whether your human risk is High, Medium, or Low</li>
              <li>• The specific gaps leaving your team exposed</li>
              <li>• What to fix first to close the biggest holes</li>
            </ul>
          </div>

          <button
            onClick={() => setStatus('assessment')}
            className="w-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] hover:opacity-90 text-white font-semibold py-3 px-6 rounded-lg transition-opacity"
          >
            Start the Risk Check
          </button>
        </div>
      </div>
    );
  }

  // Assessment
  if (status === 'assessment') {
    const question = QUESTIONS[currentIndex];
    const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
    const currentAnswer = answers.find((a) => a.questionId === question.id);

    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-lg">
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span>Phishing Risk Check</span>
              <span>Question {currentIndex + 1} of {QUESTIONS.length}</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] transition-all duration-300" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">{question.text}</h2>
            {question.helpText && <p className="text-gray-600 dark:text-gray-400 text-sm">{question.helpText}</p>}
          </div>

          <div className="space-y-3 mb-6">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleAnswer(idx)}
                className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                  currentAnswer?.optionIndex === idx
                    ? 'border-[#2813e8] bg-[#2813e8]/5'
                    : 'border-gray-200 dark:border-gray-700 hover:border-[#2813e8]/40 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <span className="text-gray-900 dark:text-gray-100">{option.text}</span>
              </button>
            ))}
          </div>

          <div className="flex justify-between">
            <button
              onClick={handleBack}
              disabled={currentIndex === 0}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Back
            </button>
            <button
              onClick={handleRestart}
              className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results
  if (status === 'results' && result) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Score */}
        <div className={`rounded-2xl border-2 p-8 ${result.bg}`}>
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">{result.level === 'High' ? '🚨' : result.level === 'Medium' ? '⚠️' : '✅'}</div>
            <h2 className={`text-3xl font-bold mb-2 ${result.color}`}>Human Risk: {result.level}</h2>
            <p className="text-gray-700 dark:text-gray-300 max-w-xl mx-auto">{result.blurb}</p>
          </div>
          <div className="bg-white/60 dark:bg-gray-800/60 rounded-lg p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Risk score</div>
            <div className={`text-3xl font-bold ${result.color}`}>{result.percent}%</div>
          </div>
        </div>

        {/* Gaps */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
            {result.gaps.length ? 'Top Gaps to Close' : 'No Major Gaps Identified'}
          </h3>
          {result.gaps.length ? (
            <ul className="space-y-3">
              {result.gaps.map((gap, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-[#2813e8]/10 text-xs font-bold text-[#2813e8]">{i + 1}</span>
                  <span className="text-gray-700 dark:text-gray-300 leading-relaxed">{gap}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">Your program looks solid. A managed service keeps training, simulations, and reporting tight as you scale.</p>
          )}
        </div>

        {/* Employee count */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            How many employees would the program cover? <span className="text-[#2813e8] font-bold">{userCount}</span>
          </label>
          <input
            type="range"
            min="1"
            max="500"
            step="1"
            value={userCount}
            onChange={(e) => setUserCount(Number(e.target.value))}
            aria-label="Number of employees"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#2813e8]"
          />
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
            <span>1</span>
            <span>250</span>
            <span>500</span>
          </div>
        </div>

        {/* Offer CTA */}
        <div className="rounded-2xl border-2 border-[#2813e8]/30 bg-[#2813e8]/5 p-6 text-center">
          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">Want these gaps closed for you?</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Inventive HQ runs a fully managed security-awareness and phishing-simulation program—continuous training, simulations, and reporting on a flat per-user basis.
          </p>
          <Link
            href="/services/security-awareness"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] px-8 py-3 font-semibold text-white shadow-lg transition-all hover:scale-105"
          >
            See Security Awareness Training
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleRestart}
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium py-3 px-6 rounded-lg transition-colors"
          >
            🔄 Retake Check
          </button>
        </div>

        {/* Lead capture */}
        <div className="mt-8">
          <LeadCaptureForm
            source="awareness"
            title="Get Your Human-Risk Posture Reviewed by an Expert"
            description="Our team will review your results and send a prioritized security-awareness program plan."
            deliverable="a prioritized security-awareness program plan"
            resultsSummary={resultsSummary}
          />
        </div>
      </div>
    );
  }

  return null;
}
